// Whether a patient carries the details an HSE integration needs, and which are missing.
//
// The decision, from Dalen and Darren (KIN-1733): a patient can be saved without these
// — a walk-in or a phone booking should not be blocked on paperwork — but nothing may be
// submitted to the HSE for them until they are recorded. Dalen's shape: "let them save
// without DOB... if they start a referral we say 'Now my friend, we need your gender,
// DOB, ethnicity etc'".
//
// Defaulting was explicitly rejected. Darren suggested recording 01/01/2001 for
// walk-ins; Dalen: "no, that'd be awful idea. Default values always cause pain ... the
// side effect would be that someone would not notice this defaulting ... best left as
// null - this is what they are invented for." A wrong date of birth silently fails
// eligibility and schedule rules; a null one obviously blocks.
//
// Requirements are per function, not global — Dalen: "we will have to come up with a
// hierarchy of mandatory data per specific function". So each surface declares what it
// needs rather than inheriting one list. Passing the wrong list is worse than passing
// none, because a warning naming an irrelevant field teaches people to dismiss it.
//
// What each surface actually needs, read off the api-practice message builders rather
// than inferred from "we populate it":
//
//   Referrals     PatientIdentification.dateOfBirth / .gender  (unvalidated server-side)
//   CDM/OCF/PP    XmlCommonGenerator:335 patient.getDateOfBirth().format(...)  → NPE
//                 XmlCommonGenerator:339 patient.getGender().substring(0, 1)   → NPE
//   Vaccines      HealthlinkVaccineSubmissionService:725 refuses a null date of birth
//                 :1575 requireRepresentableGender(patient.getGender())
//   eLab          OmlOrderBuilder:333 / :343
//   DEASP         DEASPSickCertBuilder:246 / :250 patient.getGender().getGender() → NPE
//   MN-CMS        MncmsMessageBuilder:310 / :314
//
// So date of birth and gender are required by every HealthLink surface, and those two
// are the default list.
//
// A caller may additionally ask for `country`. The vaccine form does, because it has
// always refused to submit without it, and that refusal now reads through this one notice
// instead of a hand-styled box. It is not a message requirement — the vaccine builder's
// PID.11 reads address line, city, county and postcode, never the country — so it is a
// product rule, kept as it was rather than quietly relaxed while moving it.
//
// Two others were asked for here and have been removed, both because the notice they
// produced was untrue rather than merely strict:
//
//   `financialClass`  Dalen reported it firing on any patient with no classification. The
//                     vaccine builder hardcodes PV1.20 to PUBLIC and says so in terms —
//                     "Deliberately NOT derived from patient.financialClass ... The
//                     patient's scheme is not the dose's payor type" — so the field the
//                     notice demanded is one no message reads. PPSN, suggested in its
//                     place, does not belong either: PCRS requires it on a reimbursement
//                     claim, not NIIS on a vaccination report, and a missing one already
//                     suppresses just the claim (VACCINATION_REPORT_ONLY_NOT_CLAIMABLE,
//                     KIN-1786/1790/1801) rather than blocking the send.
//
//   `ethnicity`       PID.22 is emitted only when the code is present
//                     (`StringUtils.isNotBlank(patient.getEthnicityCode())`), and is
//                     deliberately left off PCRS messages altogether, since v4.8.5 says
//                     "Ethnic Group is not part of Reimbursement message and should not be
//                     send in PCRS message to avoid rejection". A blank one omits a field;
//                     it does not fail a send. CDM's own PID.22 comes from the review's
//                     demographics (XmlCdmGenerator:110), not the patient record.
//
// The test that is attached to this file pins both, so neither comes back by accident.
//
// Ethnicity is therefore not in the default list, and neither is anything else a given
// send does not need: a warning naming an irrelevant field is the kind people learn to
// click past, which costs the notices that do matter.
//
// Prescriptions are not an HSE submission at all — PatientPrescriptionService:191 uses
// the date of birth only in an email subject, where a null renders as "(null)". Worth
// tidying, not worth blocking a script on.
//
// The backend remains the source of truth and refuses the submission regardless. The
// point of checking here is *when*: the backend can only answer once the form is filled
// and sent, and this can say so when the patient is opened.

const EARLIEST_DATE_OF_BIRTH = "1900-01-01";

// Accepted for HL7 `PID.8`. An unrecorded or non-binary value fails rather than being
// coerced, for the reason the eLab preflight already gives: sex drives laboratory
// reference ranges, so guessing it is the harmful option.
const ACCEPTED_GENDERS = ["f", "m", "female", "male"];

export const HSE_FIELD = {
  dateOfBirth: "dateOfBirth",
  gender: "gender",
  country: "country",
};

/** Every HealthLink builder sets PID.7 and PID.8, so these are needed everywhere. */
export const HSE_REQUIRED_FIELDS = [HSE_FIELD.dateOfBirth, HSE_FIELD.gender];

const isBlank = (value) => !value || !String(value).trim();

const CHECKS = {
  [HSE_FIELD.dateOfBirth]: (patient) => {
    if (isBlank(patient.dateOfBirth)) {
      return "no date of birth recorded";
    }

    const dateOfBirth = new Date(patient.dateOfBirth);

    if (Number.isNaN(dateOfBirth.getTime())) {
      return "a date of birth that isn't a valid date";
    }

    if (dateOfBirth < new Date(EARLIEST_DATE_OF_BIRTH)) {
      return "a date of birth before 1900, which the HSE rejects";
    }

    return null;
  },

  [HSE_FIELD.gender]: (patient) =>
    ACCEPTED_GENDERS.includes(String(patient.gender ?? "").trim().toLowerCase())
      ? null
      : "no gender recorded",

  // Asked for by a caller, never by default — see the note above on `HSE_REQUIRED_FIELDS`.
  // It carries the substance of a warning the vaccine form used to render itself, moved
  // here so one screen shows one kind of notice.
  [HSE_FIELD.country]: (patient) =>
    isBlank(patient.address?.country)
      ? "no country recorded in their address"
      : null,
};

/**
 * What this patient is missing, from the fields the caller says it needs.
 *
 * An absent patient returns nothing: still loading, or not yet chosen, is not a
 * violation — and a notice that flashes while data arrives trains people to ignore it.
 */
export const getMissingHseDetails = (
  patient,
  requiredFields = HSE_REQUIRED_FIELDS
) => {
  if (!patient) return [];

  return requiredFields
    .map((field) => {
      const reason = CHECKS[field]?.(patient);

      return reason ? { field, reason } : null;
    })
    .filter(Boolean);
};

export const canPatientReachHse = (patient, requiredFields) =>
  getMissingHseDetails(patient, requiredFields).length === 0;
